import React from 'react';
import Layout from '../../components/layout/Layout';

const PartnerProgramPage = () => {
  return (
    <Layout>
      <div className="ph-page">
        <div className="ph-page-header">
          <h1 className="ph-page-title">Partner Program Overview</h1>
          <p className="ph-page-subtitle">
            How our Alliance Partnership Program works and what it offers
          </p>
        </div>

        <div className="ph-page-intro">
          <p>
            The ProductHub Alliance Partnership Program is designed to create mutually beneficial 
            relationships between ProductHub and industry professionals across the supply chain. 
            We provide comprehensive support, training, and revenue opportunities while you bring 
            your expertise and industry connections.
          </p>
        </div>

        <div className="ph-panel-grid">
          {/* How It Works */}
          <div className="ph-panel">
            <div className="ph-panel-content">
              <div className="ph-panel-logo">
                <span className="material-icons">settings</span>
              </div>
              <div className="ph-panel-right">
                <div className="ph-panel-title-row">
                  <h2 className="ph-panel-title">How It Works</h2>
                </div>
                <div className="ph-panel-content-row">
                  <div className="ph-panel-text">
                    <div className="ph-panel-description">
                      <ol className="list-decimal list-inside space-y-3 text-gray-700">
                        <li>
                          <strong>Expression of Interest:</strong> Submit your application through 
                          our online form with company details and partnership goals.
                        </li>
                        <li>
                          <strong>Initial Review:</strong> Our team reviews your application and 
                          conducts preliminary research on your company and industry position.
                        </li>
                        <li>
                          <strong>Partnership Discussion:</strong> Schedule a call to discuss 
                          mutual opportunities, expectations, and partnership structure.
                        </li>
                        <li>
                          <strong>Onboarding & Training:</strong> Complete our comprehensive 
                          onboarding process including GS1 US University training.
                        </li>
                        <li>
                          <strong>Active Partnership:</strong> Begin leveraging our network, 
                          tools, and resources to grow your business.
                        </li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Partnership Models */}
          <div className="ph-panel">
            <div className="ph-panel-content">
              <div className="ph-panel-logo">
                <span className="material-icons">handshake</span>
              </div>
              <div className="ph-panel-right">
                <div className="ph-panel-title-row">
                  <h2 className="ph-panel-title">Partnership Models</h2>
                </div>
                <div className="ph-panel-content-row">
                  <div className="ph-panel-text">
                    <div className="ph-panel-description">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-2">Referral Partners</h4>
                          <p className="text-sm text-gray-600">
                            Earn commissions by referring clients to ProductHub services and solutions.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-2">Solution Partners</h4>
                          <p className="text-sm text-gray-600">
                            Integrate your products/services with our platform and ecosystem.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-2">Channel Partners</h4>
                          <p className="text-sm text-gray-600">
                            Resell ProductHub solutions with enhanced margins and support.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-2">Strategic Partners</h4>
                          <p className="text-sm text-gray-600">
                            Deep integration and co-development opportunities for long-term growth.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* What We Provide */}
          <div className="ph-panel">
            <div className="ph-panel-content">
              <div className="ph-panel-logo">
                <span className="material-icons">gift</span>
              </div>
              <div className="ph-panel-right">
                <div className="ph-panel-title-row">
                  <h2 className="ph-panel-title">What We Provide</h2>
                </div>
                <div className="ph-panel-content-row">
                  <div className="ph-panel-text">
                    <div className="ph-panel-description">
                      <ul className="list-disc list-inside space-y-2 text-gray-700">
                        <li>Comprehensive training and certification programs</li>
                        <li>Access to our extensive partner network</li>
                        <li>Marketing and sales support materials</li>
                        <li>Technical integration assistance</li>
                        <li>Revenue sharing and incentive programs</li>
                        <li>Ongoing support and relationship management</li>
                        <li>Exclusive access to industry events and webinars</li>
                        <li>Co-marketing opportunities and campaigns</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Success Metrics */}
          <div className="ph-panel">
            <div className="ph-panel-content">
              <div className="ph-panel-logo">
                <span className="material-icons">trending_up</span>
              </div>
              <div className="ph-panel-right">
                <div className="ph-panel-title-row">
                  <h2 className="ph-panel-title">Success Metrics</h2>
                </div>
                <div className="ph-panel-content-row">
                  <div className="ph-panel-text">
                    <div className="ph-panel-description">
                      <p className="mb-3">
                        We measure partnership success through various metrics including:
                      </p>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="bg-gray-50 p-3 rounded">
                          <h4 className="font-semibold text-gray-800">Revenue Growth</h4>
                          <p className="text-sm text-gray-600">Increased sales and market reach</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded">
                          <h4 className="font-semibold text-gray-800">Network Expansion</h4>
                          <p className="text-sm text-gray-600">New business connections</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded">
                          <h4 className="font-semibold text-gray-800">Customer Satisfaction</h4>
                          <p className="text-sm text-gray-600">Improved service delivery</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded">
                          <h4 className="font-semibold text-gray-800">Innovation</h4>
                          <p className="text-sm text-gray-600">New solutions and approaches</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PartnerProgramPage;
